const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
const UNIT_MINUTES = { d: MINUTES_PER_DAY, h: MINUTES_PER_HOUR, m: 1 };

function toPositiveNumber(value) {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}

function getAllocatedMinutes(levels = [], includeLastLevel = true) {
  const list = Array.isArray(levels) ? levels : [];
  const levelsToCount = includeLastLevel ? list : list.slice(0, -1);
  return levelsToCount.reduce(
    (sum, level) => sum + parseDurationToMinutes(level?.escalateAfterMinutes),
    0,
  );
}

export function parseDurationToMinutes(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return toPositiveNumber(value);

  const text = String(value).trim().toLowerCase().replace(/minutes?/g, '').trim();
  if (!text) return 0;
  if (/^\d+(\.\d+)?$/.test(text)) return toPositiveNumber(text);

  const durationTokenPattern = /(\d+(?:\.\d+)?)\s*(d|h|m)/g;
  let totalMinutes = 0;
  let foundDurationToken = false;
  let durationMatch;

  while ((durationMatch = durationTokenPattern.exec(text)) !== null) {
    foundDurationToken = true;
    totalMinutes += (UNIT_MINUTES[durationMatch[2]] || 0) * Number(durationMatch[1]);
  }

  return foundDurationToken ? toPositiveNumber(totalMinutes) : 0;
}

export function formatMinutesToDuration(value) {
  const totalMinutes = Math.round(
    typeof value === 'number' ? value : parseDurationToMinutes(value),
  );

  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '';

  const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
  const hours = Math.floor((totalMinutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  const durationParts = [];

  if (days) durationParts.push(`${days}d`);
  if (hours) durationParts.push(`${hours}h`);
  if (minutes) durationParts.push(`${minutes}m`);

  return durationParts.join('');
}

export function isValidDurationInput(value) {
  const compactDuration = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/minutes?/g, '')
    .replace(/\s+/g, '');
  if (!compactDuration) return false;
  if (!/^(\d+(?:\.\d+)?[dhm])+$/.test(compactDuration)) return false;
  return parseDurationToMinutes(value) > 0;
}

export function formatDurationInMessage(message) {
  return String(message ?? '').replace(/(\d+)\s*minutes?/gi, (_, minutes) => (
    formatMinutesToDuration(Number(minutes)) || '0m'
  ));
}

export function getAllocatedMinutesFromAllLevels(levels = []) {
  return getAllocatedMinutes(levels, true);
}

export function applyRemainingTatToLastLevel(levels = [], totalMinutes = 0) {
  if (!Array.isArray(levels) || levels.length === 0) return levels;

  const remainingMinutes = Math.max(
    0,
    parseDurationToMinutes(totalMinutes) - getAllocatedMinutes(levels, false),
  );
  const remainingLabel = formatMinutesToDuration(remainingMinutes);
  const lastLevelIndex = levels.length - 1;
  if (String(levels[lastLevelIndex]?.escalateAfterMinutes ?? '') === remainingLabel) {
    return levels;
  }

  return levels.map((level, index) => (
    index === lastLevelIndex
      ? { ...level, escalateAfterMinutes: remainingLabel }
      : level
  ));
}

export function clearLastLevelEscalationTime(levels = []) {
  if (!Array.isArray(levels) || levels.length === 0) return levels;
  const lastLevelIndex = levels.length - 1;
  return levels.map((level, index) => (
    index === lastLevelIndex
      ? { ...level, escalateAfterMinutes: '' }
      : level
  ));
}
